import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { sessionExpired } from "./SessionSlice";

export type WebptCalendarEvent = {
  id: number;
  apt_type: number;
  case_id: number;
  provider_id: number;
  patient_id: number;
  title: string;
  start_time: string;
  end_time: string;
  status: number;
}

// State interface for calendar events
export interface CalendarEventsState {
  events: WebptCalendarEvent[];
  loading: boolean;
  error: string | null;
  lastFetchedStartDate: string | null;
  lastFetchedEndDate: string | null;
}

// Initial state
const initialState: CalendarEventsState = {
  events: [],
  loading: false,
  error: null,
  lastFetchedStartDate: null,
  lastFetchedEndDate: null,
};

// Async thunk for fetching calendar events
export const fetchCalendarEvents = createAsyncThunk<
  { events: WebptCalendarEvent[] },
  { startDate: string; endDate: string; token: string },
  { rejectValue: Error }
>("calendarEvents/fetchEvents", async ({ startDate, endDate, token }, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/webpt/schedule`,
      {
        params: {
          start_date: startDate, // ISO format date string
          end_date: endDate, // ISO format date string
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.data) {
      throw new Error("No data received");
    }
    
    return { 
      events: response.data.map((event: any) => ({
        id: event.id,
        apt_type: event.apt_type,
        case_id: event.case_id,
        provider_id: event.c_id,
        patient_id: event.p_id,
        title: event.title,
        start_time: event.startString,
        end_time: event.endString,
        status: event.status,
      }))
    };
  } catch (error) {
    if ((error as AxiosError).response?.status === 401) {
      sessionExpired(true);
    }
    return rejectWithValue(new Error("Failed to fetch calendar events: " + (error as Error).message));
  }
});

// Create the calendar events slice
const calendarEventsSlice = createSlice({
  name: "calendarEvents",
  initialState,
  reducers: {
    clearEvents: (state) => {
      state.events = [];
      state.lastFetchedStartDate = null;
      state.lastFetchedEndDate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendarEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        state.events = action.payload.events;
        state.loading = false;
        state.error = null;
        state.lastFetchedStartDate = action.meta.arg.startDate;
        state.lastFetchedEndDate = action.meta.arg.endDate;
      })
      .addCase(fetchCalendarEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "An error occurred while fetching calendar events";
      });
  },
});

export const { clearEvents } = calendarEventsSlice.actions;
export default calendarEventsSlice.reducer; 